import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Search,
  Users,
  Kanban,
  ClipboardList,
  FileSearch,
  BarChart3,
  Settings as SettingsIcon,
  LogOut,
  Bell,
  Menu,
  X,
  ExternalLink,
} from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import api from "@/lib/apiClient";

const NAV = [
  { to: "/admin/dashboard", end: true, label: "Overview", icon: LayoutDashboard },
  { to: "/admin/dashboard/lead-generator", label: "Lead Generator", icon: Search },
  { to: "/admin/dashboard/leads", label: "CRM", icon: Users },
  { to: "/admin/dashboard/pipeline", label: "Pipeline", icon: Kanban },
  { to: "/admin/dashboard/follow-ups", label: "Follow-Ups", icon: ClipboardList, phase: 3 },
  { to: "/admin/dashboard/website-audits", label: "Website Audits", icon: FileSearch, phase: 3 },
  { to: "/admin/dashboard/analytics", label: "Analytics", icon: BarChart3, phase: 3 },
  { to: "/admin/dashboard/settings", label: "Settings", icon: SettingsIcon },
];

export default function AdminLayout() {
  const { admin, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const { data } = await api.get("/admin/notifications");
        if (mounted) setUnread(data.unread || 0);
      } catch {
        /* ignore */
      }
    };
    load();
    const iv = setInterval(load, 45000);
    return () => {
      mounted = false;
      clearInterval(iv);
    };
  }, []);

  const doLogout = async () => {
    await logout();
    nav("/admin", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex" data-testid="admin-shell">
      {/* Sidebar */}
      <aside
        data-testid="admin-sidebar"
        className={`
          fixed lg:sticky top-0 left-0 h-screen w-72 shrink-0 bg-[#0B0B0B] border-r border-white/10
          transform transition-transform duration-300 z-40
          ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
        `}
      >
        <div className="h-16 px-6 flex items-center justify-between border-b border-white/10">
          <a href="/admin/dashboard" className="flex items-center gap-3">
            <span className="font-display text-2xl tracking-tight leading-none text-white">
              <span className="italic">A</span>
              <span className="italic -ml-1.5">S</span>
            </span>
            <span className="text-[10px] tracking-[0.32em] uppercase text-white/60 border-l border-white/15 pl-3">
              Admin
            </span>
          </a>
          <button
            className="lg:hidden text-white/60"
            onClick={() => setOpen(false)}
            aria-label="Close menu"
          >
            <X size={18} />
          </button>
        </div>

        <nav className="p-3 space-y-1">
          {NAV.map(({ to, end, label, icon: Icon, phase }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              onClick={() => setOpen(false)}
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `group flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                  isActive
                    ? "bg-blue-500/10 text-white border border-blue-500/30"
                    : "text-white/60 hover:text-white hover:bg-white/[0.03] border border-transparent"
                }`
              }
            >
              <span className="flex items-center gap-3 min-w-0">
                <Icon size={16} className="shrink-0" />
                <span className="text-sm font-medium truncate">{label}</span>
              </span>
              {phase && (
                <span className="text-[9px] tracking-[0.22em] uppercase text-white/30 border border-white/10 px-1.5 py-0.5 rounded-full">
                  P{phase}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-white/10 bg-[#0B0B0B]">
          <div className="px-3 py-2 mb-2">
            <div className="text-xs text-white/40">Signed in as</div>
            <div className="text-sm text-white font-medium truncate">
              {admin?.username || "admin"}
            </div>
          </div>
          <button
            data-testid="admin-logout"
            onClick={doLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-white/60 hover:text-white hover:bg-white/[0.03] transition-colors"
          >
            <LogOut size={16} />
            <span className="text-sm font-medium">Log out</span>
          </button>
        </div>
      </aside>

      {/* Backdrop on mobile */}
      {open && (
        <div
          className="fixed inset-0 bg-black/60 z-30 lg:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Topbar */}
        <header className="h-16 border-b border-white/10 flex items-center justify-between px-6 sticky top-0 bg-[#0A0A0A]/85 backdrop-blur-xl z-20">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden text-white/70"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
            >
              <Menu size={20} />
            </button>
            <div className="text-sm text-white/50">
              <span className="text-white/40">Almonte Sites</span>
              <span className="mx-2 text-white/20">/</span>
              <span className="text-white">Admin</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-2 text-xs text-white/50 hover:text-white transition-colors px-3 py-1.5 rounded-full border border-white/10 hover:border-white/25"
            >
              View site
              <ExternalLink size={12} />
            </a>
            <div className="relative">
              <button
                className="h-9 w-9 rounded-full border border-white/10 hover:border-white/25 flex items-center justify-center text-white/70 hover:text-white transition-colors"
                aria-label="Notifications"
                data-testid="topbar-notifications"
              >
                <Bell size={15} />
              </button>
              {unread > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-blue-500 text-[10px] text-white flex items-center justify-center font-semibold">
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 p-6 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
